// src/components/shared/ExplanationTooltip.tsx - גרסה מתוקנת עם pinning ומיקום נכון
import React, { useState, useRef, useEffect, useCallback } from "react";
import { ExplanationData } from "../../services/enduser/calculatorService";

interface ExplanationTooltipProps {
  stepKey: string;
  explanations: Record<string, ExplanationData>;
  className?: string;
}

const ExplanationTooltip: React.FC<ExplanationTooltipProps> = ({
  stepKey,
  explanations,
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPinned, setIsPinned] = useState(false); // מצב "נעוץ" - נשאר פתוח עד לחיצה מחוץ
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const explanation = explanations[stepKey];

  // פונקציה לחישוב מיקום אופטימלי
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // גודל משוער של הטולטיפ (נעדכן אחרי רינדור אם צריך)
    const tooltipWidth = tooltipRef.current?.offsetWidth || 320;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 100;

    let top = containerRect.bottom + 8; // 8px מתחת לאלמנט
    let left = containerRect.left - tooltipWidth / 2 + containerRect.width / 2; // ממורכז

    // בדיקה אם הטולטיפ יוצא מהמסך מימין
    if (left + tooltipWidth > viewport.width - 20) {
      left = viewport.width - tooltipWidth - 20;
    }

    // בדיקה אם הטולטיפ יוצא מהמסך משמאל
    if (left < 20) {
      left = 20;
    }

    // בדיקה אם הטולטיפ יוצא מהמסך מלמטה - הצג מעל
    if (top + tooltipHeight > viewport.height - 20) {
      top = containerRect.top - tooltipHeight - 8;
    }

    // בדיקה אם הטולטיפ יוצא מהמסך מלמעלה
    if (top < 20) {
      top = 20;
    }

    return { top, left };
  }, []);

  // פונקציה להצגת הטולטיפ (hover)
  const showTooltip = useCallback(() => {
    if (isPinned) return; // אם נעוץ, לא משנים

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // חישוב מיקום לפני הצגה
    const newPosition = calculatePosition();
    if (newPosition) {
      setPosition(newPosition);
    }
    setIsVisible(true);
  }, [isPinned, calculatePosition]);

  // פונקציה להסתרת הטולטיפ (hover)
  const hideTooltip = useCallback(() => {
    if (isPinned) return; // אם נעוץ, לא מסתירים

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setPosition(null);
    }, 150);
  }, [isPinned]);

  // פונקציה לביטול הסתרה אם נכנסים לטולטיפ
  const cancelHide = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // פונקציה ללחיצה על סימן השאלה - toggle pinned
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (isPinned) {
        // אם כבר נעוץ - סוגר
        setIsPinned(false);
        setIsVisible(false);
        setPosition(null);
      } else {
        // נועץ את הטולטיפ
        const newPosition = calculatePosition();
        if (newPosition) {
          setPosition(newPosition);
        }
        setIsVisible(true);
        setIsPinned(true);
      }
    },
    [isPinned, calculatePosition]
  );

  // סגירה בלחיצה מחוץ לטולטיפ
  useEffect(() => {
    if (!isPinned) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      // בדיקה אם הלחיצה היא מחוץ לטולטיפ ומחוץ לסימן השאלה
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(target) &&
        containerRef.current &&
        !containerRef.current.contains(target)
      ) {
        setIsPinned(false);
        setIsVisible(false);
        setPosition(null);
      }
    };

    // הוספת listener עם delay קטן כדי לא לתפוס את הלחיצה הנוכחית
    setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isPinned]);

  // ניקוי timeout כשהקומפוננט מתפרק
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // עדכון מיקום כשהחלון משתנה
  useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        const newPosition = calculatePosition();
        if (newPosition) {
          setPosition(newPosition);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [isVisible, calculatePosition]);

  // עדכון מיקום אחרי שהטולטיפ נרנדר (לדיוק מקסימלי)
  useEffect(() => {
    if (isVisible && tooltipRef.current && position) {
      const newPosition = calculatePosition();
      if (
        newPosition &&
        (newPosition.top !== position.top || newPosition.left !== position.left)
      ) {
        setPosition(newPosition);
      }
    }
  }, [isVisible, calculatePosition]);

  // אם אין הסבר עבור ה-step הזה, לא מציגים כלום
  if (!explanation) {
    return null;
  }

  return (
    <>
      {/* סימן השאלה */}
      <div
        ref={containerRef}
        className={`print-exclude inline-flex items-center justify-center w-4 h-4 ml-1 text-xs text-blue-600 bg-blue-100 rounded-full cursor-pointer hover:bg-blue-200 transition-colors select-none ${
          isPinned ? "bg-blue-300 ring-2 ring-blue-400" : ""
        } ${className}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label={`הסבר עבור ${explanation.title}`}
        aria-expanded={isVisible}
      >
        ?
      </div>

      {/* הטולטיפ עצמו - מוצג רק כשיש מיקום */}
      {isVisible && position && (
        <div
          ref={tooltipRef}
          className={`fixed z-[9999999] max-w-sm p-3 text-sm text-white bg-gray-900 rounded-lg shadow-xl ${
            isPinned ? "ring-2 ring-blue-400" : ""
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            direction: "rtl",
          }}
          onMouseEnter={cancelHide}
          onMouseLeave={hideTooltip}
        >
          {/* כותרת */}
          {explanation.title && (
            <div className="font-bold text-blue-200 mb-1 flex items-center justify-between">
              <span>{explanation.title}</span>
              {isPinned && (
                <button
                  onClick={() => {
                    setIsPinned(false);
                    setIsVisible(false);
                    setPosition(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors mr-2"
                  aria-label="סגור"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* תוכן ההסבר */}
          <div className="text-gray-100 leading-relaxed">
            {explanation.text}
          </div>

          {/* הודעה קטנה כשנעוץ */}
          {isPinned && (
            <div className="text-xs text-gray-400 mt-2 border-t border-gray-700 pt-2">
              💡 לחץ בכל מקום לסגירה
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ExplanationTooltip;
